// src/components/Navbar.jsx
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaDiscord, FaTwitch, FaTiktok, FaInstagram } from "react-icons/fa";
import Logo from "../assets/logo/pvalorlogo.png";

function Navbar() {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Roster", path: "/roster" },
    { name: "Creators", path: "/creators" },
    { name: "Announcements", path: "/announcements" },
    { name: "Events", path: "/events" },
    { name: "Shop", path: "/shop" },
    { name: "Contact", path: "/contact" },
    { name: "Join", path: "/join", highlight: true },
  ];

  const socialLinks = [
    { icon: FaDiscord, url: "https://discord.gg/SGXXCMpCxf", label: "Discord" },
    { icon: FaTwitch, url: "https://www.twitch.tv/primevaloresports", label: "Twitch" },
    { icon: FaTiktok, url: "https://www.tiktok.com/@primevaloresports", label: "TikTok" },
    { icon: FaInstagram, url: "https://www.instagram.com/primevaloresports", label: "Instagram" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setNavOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/95 backdrop-blur-md shadow-lg py-2"
          : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-3 group">
          <img
            src={Logo}
            alt="Prime Valor Logo"
            className="h-10 w-auto group-hover:scale-105 transition-transform"
          />
          <span className="text-xl font-black tracking-tight text-white uppercase hidden sm:block">
            Prime <span className="text-red-600">Valor</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-1">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`px-4 py-2 text-sm uppercase font-semibold tracking-wide transition-all duration-200 rounded ${
                link.highlight
                  ? "bg-red-600 hover:bg-red-700 text-white ml-2"
                  : isActive(link.path)
                  ? "text-red-500 bg-red-500/10"
                  : "text-white hover:text-red-500 hover:bg-white/5"
              }`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Desktop Social Links */}
        <div className="hidden lg:flex items-center space-x-3">
          {socialLinks.map((social, index) => (
            <a
              key={index}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.label}
              className="text-gray-400 hover:text-white transition-colors p-2"
            >
              <social.icon size={18} />
            </a>
          ))}
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setNavOpen(!navOpen)}
          className="lg:hidden text-white p-2 hover:bg-white/10 rounded transition-colors"
          aria-label="Toggle menu"
        >
          {navOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 top-[60px] bg-black/98 backdrop-blur-md transition-all duration-300 ${
          navOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <nav className="flex flex-col p-6 space-y-2">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`px-4 py-4 text-lg uppercase font-semibold tracking-wide transition-colors rounded-lg ${
                isActive(link.path)
                  ? "text-red-500 bg-red-500/10"
                  : "text-white hover:text-red-500 hover:bg-white/5"
              }`}
            >
              {link.name}
            </Link>
          ))}

          {/* Mobile Social Links */}
          <div className="flex items-center space-x-4 pt-6 mt-4 border-t border-gray-800">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="text-gray-400 hover:text-white transition-colors p-3 bg-gray-900 rounded-lg"
              >
                <social.icon size={20} />
              </a>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Navbar;
